import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { KeyRound, Copy, Plus, Users, User, CheckCircle2, Circle, Send, Sparkles, Clock, Mail, Loader2, CreditCard, FileText, ExternalLink } from "lucide-react";
import { aiExtensions, usePricing } from "@/lib/pricing";

const PLAN_LABELS = { einzel: "Einzel", team: "Team" };
const SOURCE_LABELS = {
  landing: "Landing-Page",
  dashboard: "Dashboard",
  briefing: "Lichtbriefing",
  ampel: "Normen-Ampel",
  unbekannt: "Unbekannt",
};

export default function AdminLicenses() {
  const { user } = useAuth();
  const pricing = usePricing();
  const [count, setCount] = useState(10);
  const [batch, setBatch] = useState("");
  const [plan, setPlan] = useState("einzel");
  const [generating, setGenerating] = useState(false);
  const [aiCount, setAiCount] = useState(20);
  const [aiDays, setAiDays] = useState(30);
  const [aiBatch, setAiBatch] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [teamCode, setTeamCode] = useState("");
  const [teamDays, setTeamDays] = useState(30);
  const [extendingTeam, setExtendingTeam] = useState(false);

  const listQ = useQuery({
    queryKey: ["licenses"],
    queryFn: async () => (await api.get("/admin/licenses")).data,
  });

  const inquiryQ = useQuery({
    queryKey: ["inquiry-stats"],
    queryFn: async () => (await api.get("/admin/inquiry-stats")).data,
  });

  const aiQ = useQuery({
    queryKey: ["ai-codes"],
    queryFn: async () => (await api.get("/admin/ai-codes")).data,
  });

  const paymentsQ = useQuery({
    queryKey: ["admin-payments"],
    queryFn: async () => (await api.get("/admin/payments")).data,
  });

  const invoicesQ = useQuery({
    queryKey: ["admin-invoices"],
    queryFn: async () => (await api.get("/admin/invoices")).data,
  });

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const plans = listQ.data?.plans || {};

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/admin/licenses/generate", { count: Number(count), batch: batch || null, plan });
      toast.success(`${r.data.created} ${PLAN_LABELS[plan]}-Lizenzschlüssel erzeugt`);
      listQ.refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const keys = listQ.data?.keys || [];
  // Frisch = noch von niemandem eingelöst -> kann manuell an Käufer ausgegeben werden
  const freshCodes = keys.filter((k) => (k.used_seats || 0) === 0 && (k.plan || "einzel") === plan).map((k) => k.code);

  const copyFresh = async () => {
    if (!freshCodes.length) {
      toast.error(`Keine frischen ${PLAN_LABELS[plan]}-Codes vorhanden`);
      return;
    }
    await navigator.clipboard.writeText(freshCodes.join("\n"));
    toast.success(`${freshCodes.length} frische ${PLAN_LABELS[plan]}-Codes kopiert`);
  };

  const aiCodes = aiQ.data?.codes || [];
  const freshAiCodes = aiCodes.filter((c) => c.status === "unused" && (c.days || 30) === Number(aiDays)).map((c) => c.code);

  const generateAi = async () => {
    setAiGenerating(true);
    try {
      const r = await api.post("/admin/ai-codes/generate", { count: Number(aiCount), days: Number(aiDays), batch: aiBatch || null });
      toast.success(`${r.data.created} KI-Verlängerungs-Codes (${r.data.days} Tage) erzeugt`);
      aiQ.refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAiGenerating(false);
    }
  };

  const copyFreshAi = async () => {
    if (!freshAiCodes.length) {
      toast.error(`Keine frischen ${aiDays}-Tage-Codes vorhanden`);
      return;
    }
    await navigator.clipboard.writeText(freshAiCodes.join("\n"));
    toast.success(`${freshAiCodes.length} frische ${aiDays}-Tage-Codes kopiert`);
  };

  const teamCodes = keys.filter((k) => (k.plan || "einzel") === "team" && (k.used_seats || 0) > 0);

  const extendTeam = async () => {
    if (!teamCode) {
      toast.error("Bitte wählen Sie eine Team-Lizenz aus.");
      return;
    }
    setExtendingTeam(true);
    try {
      const r = await api.post(`/admin/licenses/${teamCode}/extend-team-ai`, { days: Number(teamDays) });
      toast.success(`${r.data.members_extended} Teammitglied(er) um ${r.data.days_added} Tage verlängert`);
      listQ.refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setExtendingTeam(false);
    }
  };

  const stats = [
    ["Codes gesamt", listQ.data?.total ?? 0],
    ["Plätze gesamt", listQ.data?.total_seats ?? 0],
    ["Belegt", listQ.data?.used_seats ?? 0],
    ["Frei", listQ.data?.free_seats ?? 0],
  ];

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        <div className="flex items-center gap-3 mb-2">
          <KeyRound className="h-6 w-6 text-gold" />
          <h1 className="font-heading font-extrabold text-3xl">Lizenzschlüssel</h1>
        </div>
        <p className="text-muted-foreground mb-8 max-w-2xl">
          Erzeugen Sie Codes je Tarif für den manuellen Verkauf oder Sonderfälle – Online-Käufe werden
          automatisch über Stripe/AbaNinja abgewickelt. <strong className="text-foreground/90">Einzel</strong> = 1 Platz · 25 Coach / 8 Briefings
          pro Tag. <strong className="text-foreground/90">Team</strong> = 10 Plätze · 40 Coach / 15 Briefings pro Tag und Nutzer.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {stats.map(([label, val], i) => (
            <div key={i} className="rounded-2xl glass px-5 py-4 text-center" data-testid={`license-stat-${i}`}>
              <div className="font-heading font-bold text-2xl text-gold">{val}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl glass-strong p-6 mb-8" data-testid="payments-section">
          <div className="flex items-center gap-2.5 mb-4">
            <CreditCard className="h-5 w-5 text-gold" />
            <h2 className="font-heading font-bold text-lg">Zahlungen (Stripe)</h2>
            <span className="ml-auto font-heading font-bold text-2xl text-gold" data-testid="payments-revenue">
              CHF {paymentsQ.data?.revenue ?? 0}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-5">
            {[["Transaktionen", paymentsQ.data?.total ?? 0], ["Bezahlt", paymentsQ.data?.paid ?? 0], ["Umsatz CHF", paymentsQ.data?.revenue ?? 0]].map(([label, val], i) => (
              <div key={i} className="rounded-xl glass px-4 py-3 text-center" data-testid={`payments-stat-${i}`}>
                <div className="font-heading font-bold text-xl text-gold">{val}</div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
              </div>
            ))}
          </div>
          {paymentsQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Lädt…</p>
          ) : (paymentsQ.data?.total ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="payments-empty">
              Noch keine Zahlungen. Käufe über die Lizenz- und KI-Verlängerungs-Bezahlung erscheinen hier.
            </p>
          ) : (
            <div className="space-y-3">
              {paymentsQ.data.payments.slice(0, 50).map((p) => {
                const paid = p.payment_status === "paid";
                return (
                  <div key={p.session_id} data-testid={`payment-row-${p.session_id}`} className="rounded-xl glass px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{p.description || p.kind}</span>
                        <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 text-gold/90 hover:text-gold">
                          <Mail className="h-3.5 w-3.5" /> {p.email}
                        </a>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {p.created_at ? new Date(p.created_at).toLocaleString("de-CH") : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-heading font-bold text-gold">CHF {p.amount}</span>
                      {paid ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
                          <CheckCircle2 className="h-4 w-4" /> bezahlt
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs text-amber-300">
                          <Circle className="h-3.5 w-3.5" /> {p.payment_status || "offen"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-2xl glass-strong p-6 mb-8" data-testid="invoices-section">
          <div className="flex items-center gap-2.5 mb-4">
            <FileText className="h-5 w-5 text-gold" />
            <h2 className="font-heading font-bold text-lg">Rechnungen (AbaNinja · auf Rechnung)</h2>
            <span className="ml-auto font-heading font-bold text-2xl text-gold" data-testid="invoices-amount">
              CHF {invoicesQ.data?.amount ?? 0}
            </span>
          </div>
          {invoicesQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Lädt…</p>
          ) : (invoicesQ.data?.total ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="invoices-empty">
              Noch keine Rechnungen. Team-Bestellungen «auf Rechnung» erscheinen hier mit QR-Rechnung & Portal-Link.
            </p>
          ) : (
            <div className="space-y-3">
              {invoicesQ.data.invoices.slice(0, 50).map((inv) => (
                <div key={inv.invoice_uuid} data-testid={`invoice-row-${inv.invoice_number}`} className="rounded-xl glass px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{inv.invoice_number}</span>
                      <span className="text-foreground/70">{inv.company_name}</span>
                      <a href={`mailto:${inv.email}`} className="inline-flex items-center gap-1 text-gold/90 hover:text-gold">
                        <Mail className="h-3.5 w-3.5" /> {inv.email}
                      </a>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Zahlbar bis {inv.due_date} · erstellt {inv.created_at ? new Date(inv.created_at).toLocaleDateString("de-CH") : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold text-gold">CHF {inv.amount}</span>
                    {inv.portal_url && (
                      <a href={inv.portal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs glass px-2.5 py-1.5 rounded-lg hover:glow-gold transition-all">
                        <ExternalLink className="h-3.5 w-3.5 text-gold" /> Portal
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl glass-strong p-6 mb-8" data-testid="inquiry-stats">
          <div className="flex items-center gap-2.5 mb-4">
            <Send className="h-5 w-5 text-gold" />
            <h2 className="font-heading font-bold text-lg">Projektanfragen (Button-Klicks)</h2>
            <span className="ml-auto font-heading font-bold text-2xl text-gold" data-testid="inquiry-total">
              {inquiryQ.data?.total ?? 0}
            </span>
          </div>
          {inquiryQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Lädt…</p>
          ) : (inquiryQ.data?.total ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="inquiry-empty">
              Noch keine Klicks auf „Lichtplanung/Projekt anfragen". Quellen: Landing, Dashboard, Lichtbriefing, Normen-Ampel.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(inquiryQ.data.by_source).map(([src, cnt]) => (
                <div key={src} className="rounded-xl glass px-4 py-3" data-testid={`inquiry-source-${src}`}>
                  <div className="font-heading font-bold text-xl">{cnt}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{SOURCE_LABELS[src] || src}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl glass-strong p-6 mb-8" data-testid="team-ai-extend">
          <div className="flex items-center gap-2.5 mb-2">
            <Users className="h-5 w-5 text-gold" />
            <h2 className="font-heading font-bold text-lg">Team-Lizenz · KI zentral verlängern</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
            Verlängern Sie die KI-Laufzeit einer Team-Lizenz zentral über das Büro. Ein Klick verlängert alle
            registrierten Teammitglieder dieser Lizenz gleichzeitig (Tage werden auf das bestehende Ablaufdatum
            gestapelt) und informiert sie automatisch per E-Mail.
          </p>
          {teamCodes.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="team-ai-extend-empty">
              Noch keine Team-Lizenz mit registrierten Mitgliedern vorhanden.
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-4">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Team-Lizenz</span>
                <select
                  value={teamCode}
                  onChange={(e) => setTeamCode(e.target.value)}
                  data-testid="team-ai-code-select"
                  className="mt-1.5 w-64 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 transition-colors text-foreground [&>option]:bg-[#0a0d12]"
                >
                  <option value="">– Lizenz wählen –</option>
                  {teamCodes.map((k) => (
                    <option key={k.code} value={k.code}>{k.code} · {k.used_seats}/{k.seats} belegt</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Dauer</span>
                <select
                  value={teamDays}
                  onChange={(e) => setTeamDays(Number(e.target.value))}
                  data-testid="team-ai-days-select"
                  className="mt-1.5 w-56 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 transition-colors text-foreground [&>option]:bg-[#0a0d12]"
                >
                  {aiExtensions(pricing, "team").map((x) => (
                    <option key={x.days} value={x.days}>{x.days} Tage · {x.price}</option>
                  ))}
                </select>
              </label>
              <button
                onClick={extendTeam}
                disabled={extendingTeam || !teamCode}
                data-testid="team-ai-extend-button"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all disabled:opacity-60"
              >
                {extendingTeam ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {extendingTeam ? "Verlängere…" : "Team verlängern"}
              </button>
            </div>
          )}
        </div>

        <div className="rounded-2xl glass-strong p-6 mb-8">
          <h2 className="font-heading font-bold text-lg mb-4">Neue Codes erzeugen</h2>
          <div className="flex flex-wrap items-end gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Tarif</span>
              <select
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                data-testid="license-plan-select"
                className="mt-1.5 w-44 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 transition-colors text-foreground [&>option]:bg-[#0a0d12]"
              >
                <option value="einzel">Einzel · 1 Platz</option>
                <option value="team">Team · 10 Plätze</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Anzahl Codes</span>
              <input
                type="number"
                min={1}
                max={500}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                data-testid="license-count-input"
                className="mt-1.5 w-28 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 transition-colors"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Charge (optional)</span>
              <input
                type="text"
                value={batch}
                onChange={(e) => setBatch(e.target.value)}
                placeholder="z. B. Launch-2026"
                data-testid="license-batch-input"
                className="mt-1.5 w-52 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 transition-colors"
              />
            </label>
            <button
              onClick={generate}
              disabled={generating}
              data-testid="license-generate-button"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all disabled:opacity-60"
            >
              <Plus className="h-4 w-4" /> {generating ? "Erzeuge…" : "Erzeugen"}
            </button>
            <button
              onClick={copyFresh}
              data-testid="license-copy-fresh-button"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full glass hover:glow-gold transition-all"
            >
              <Copy className="h-4 w-4 text-gold" /> Frische {PLAN_LABELS[plan]}-Codes kopieren
            </button>
          </div>
        </div>

        <div className="rounded-2xl glass overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b hairline">
            <div className="col-span-4">Code</div>
            <div className="col-span-2">Tarif</div>
            <div className="col-span-3">Plätze</div>
            <div className="col-span-3">Status</div>
          </div>
          {listQ.isLoading ? (
            <div className="px-5 py-8 text-center text-muted-foreground">Lädt…</div>
          ) : keys.length === 0 ? (
            <div className="px-5 py-8 text-center text-muted-foreground" data-testid="license-empty">
              Noch keine Codes erzeugt.
            </div>
          ) : (
            keys.map((k) => {
              const seats = k.seats || 1;
              const usedSeats = k.used_seats || 0;
              const isTeam = (k.plan || "einzel") === "team";
              return (
                <div
                  key={k.code}
                  data-testid={`license-row-${k.code}`}
                  className="grid grid-cols-12 px-5 py-3 items-center border-b hairline last:border-0 text-sm"
                >
                  <div className="col-span-4 font-mono truncate">{k.code}</div>
                  <div className="col-span-2">
                    <span className="inline-flex items-center gap-1.5 text-xs text-foreground/80">
                      {isTeam ? <Users className="h-3.5 w-3.5 text-gold" /> : <User className="h-3.5 w-3.5 text-gold" />}
                      {PLAN_LABELS[k.plan] || "Einzel"}
                    </span>
                  </div>
                  <div className="col-span-3 font-mono text-foreground/85" data-testid={`license-seats-${k.code}`}>
                    {usedSeats} / {seats}
                  </div>
                  <div className="col-span-3">
                    <SeatStatus used={usedSeats} seats={seats} />
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="rounded-2xl glass-strong p-6 mt-8" data-testid="ai-codes-section">
          <div className="flex items-center gap-2.5 mb-2">
            <Sparkles className="h-5 w-5 text-gold" />
            <h2 className="font-heading font-bold text-lg">KI-Verlängerungs-Codes</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5 max-w-2xl">
            Jeder Kurszugang enthält 30 Tage KI (Coach + Briefing). Danach verlängern Nutzer ihren KI-Zugang
            direkt online über Stripe – oder mit diesen manuell ausgestellten Codes (z.B. für Sonderfälle).
            Codes sind einmal einlösbar und stapeln sich auf das bestehende Ablaufdatum.
          </p>

          <div className="grid grid-cols-3 gap-4 mb-6">
            {[["Codes gesamt", aiQ.data?.total ?? 0], ["Eingelöst", aiQ.data?.used ?? 0], ["Frei", aiQ.data?.unused ?? 0]].map(([label, val], i) => (
              <div key={i} className="rounded-xl glass px-4 py-3 text-center" data-testid={`ai-code-stat-${i}`}>
                <div className="font-heading font-bold text-xl text-gold">{val}</div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-4 mb-6">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Dauer</span>
              <select
                value={aiDays}
                onChange={(e) => setAiDays(Number(e.target.value))}
                data-testid="ai-days-select"
                className="mt-1.5 w-44 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 transition-colors text-foreground [&>option]:bg-[#0a0d12]"
              >
                <option value={30}>30 Tage (Monat)</option>
                <option value={90}>90 Tage (Quartal)</option>
                <option value={365}>365 Tage (Jahr)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Anzahl Codes</span>
              <input
                type="number"
                min={1}
                max={500}
                value={aiCount}
                onChange={(e) => setAiCount(e.target.value)}
                data-testid="ai-count-input"
                className="mt-1.5 w-28 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 transition-colors"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Charge (optional)</span>
              <input
                type="text"
                value={aiBatch}
                onChange={(e) => setAiBatch(e.target.value)}
                placeholder="z. B. KI-Monat-2026"
                data-testid="ai-batch-input"
                className="mt-1.5 w-52 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold/50 transition-colors"
              />
            </label>
            <button
              onClick={generateAi}
              disabled={aiGenerating}
              data-testid="ai-generate-button"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all disabled:opacity-60"
            >
              <Plus className="h-4 w-4" /> {aiGenerating ? "Erzeuge…" : "Erzeugen"}
            </button>
            <button
              onClick={copyFreshAi}
              data-testid="ai-copy-fresh-button"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full glass hover:glow-gold transition-all"
            >
              <Copy className="h-4 w-4 text-gold" /> Frische {aiDays}-Tage-Codes kopieren
            </button>
          </div>

          <div className="rounded-2xl glass overflow-hidden">
            <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-widest text-muted-foreground border-b hairline">
              <div className="col-span-6">Code</div>
              <div className="col-span-3">Dauer</div>
              <div className="col-span-3">Status</div>
            </div>
            {aiQ.isLoading ? (
              <div className="px-5 py-8 text-center text-muted-foreground">Lädt…</div>
            ) : aiCodes.length === 0 ? (
              <div className="px-5 py-8 text-center text-muted-foreground" data-testid="ai-codes-empty">
                Noch keine KI-Codes erzeugt.
              </div>
            ) : (
              aiCodes.map((c) => (
                <div
                  key={c.code}
                  data-testid={`ai-code-row-${c.code}`}
                  className="grid grid-cols-12 px-5 py-3 items-center border-b hairline last:border-0 text-sm"
                >
                  <div className="col-span-6 font-mono truncate">{c.code}</div>
                  <div className="col-span-3">
                    <span className="inline-flex items-center gap-1.5 text-foreground/85">
                      <Clock className="h-3.5 w-3.5 text-gold" /> {c.days || 30} Tage
                    </span>
                  </div>
                  <div className="col-span-3">
                    {c.status === "used" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-amber-glow">
                        <CheckCircle2 className="h-3.5 w-3.5" /> eingelöst
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
                        <Circle className="h-3.5 w-3.5" /> frei
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function SeatStatus({ used, seats }) {
  if (used >= seats) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-glow">
        <CheckCircle2 className="h-3.5 w-3.5" /> voll belegt
      </span>
    );
  }
  if (used > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-300">
        <Circle className="h-3.5 w-3.5" /> teilweise
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
      <Circle className="h-3.5 w-3.5" /> frei
    </span>
  );
}
